﻿import compareArrays from '@tk/utilities/tk.array.compare';
import TKCustomElementFactory from '@tk/utilities/tk.custom.element.factory';
import { fetchRequest } from '@tk/utilities/tk.fetch';
import render from '@tk/utilities/tk.render';

interface Attribute {
    attr: string;
    label: string;
    value?: string;
}

interface ArticleVariant {
    id: string;
    attributes: Attribute[];
}

interface Selection {
    wrapper: HTMLElement;
    fieldElement: HTMLSelectElement;
    attribute: string;
    label: string;
    values: Set<string>;
}

interface ArticleVariantsResponse {
    attributes: Attribute[];
    variants: ArticleVariant[];
}

const ASYNC_CONTENT_RELOAD_URL_ATTR = 'data-tk-selection-reload-url';

export default class TKSelectRedirect extends TKCustomElementFactory {
    selectElement?: HTMLSelectElement;
    queryName: string;
    form?: HTMLFormElement;
    asyncURL?: string;
    selections: Selection[] = [];
    variants: ArticleVariant[] = [];
    currentId?: string;

    constructor() {
        super();

        this.selectElement = this.querySelector('select') || undefined;
        this.queryName = this.getAttribute('data-tk-query-name') || 'query';
        this.form = this.querySelector('form') || undefined;
        this.asyncURL = this.getAttribute('data-tk-async-url') || undefined;
        this.currentId = this.getAttribute('data-tk-current-id') || undefined;
    }

    connectedCallback(): void {
        if (this.asyncURL) {
            this.getData();
        } else if (this.form) this.registerInputListener();
        else {
            if (!this.selectElement) throw new Error('TKSelectRedirect: Select element is missing!');
            this.registerSelectListener();
        }
    }

    registerInputListener() {
        const fieldElements = this.form!.querySelectorAll('input');
        const onInputByField = this.handleInput.bind(this);
        fieldElements.forEach((fieldElements) => {
            this.pushListener({ event: 'input', element: fieldElements, action: onInputByField });
        });
    }

    registerSelectListener() {
        const onInputBySelect = this.handleSelect.bind(this);
        this.pushListener({ event: 'input', element: this.selectElement!, action: onInputBySelect });
    }

    handleSelect() {
        const selectedOption = this.selectElement?.options.item(this.selectElement.selectedIndex);
        if (!selectedOption) return;
        this.redirectByValue(selectedOption.value);
    }

    handleInput(event: KeyboardEvent) {
        const { value } = event.target as HTMLInputElement;
        this.redirectByValue(value);
    }

    redirectByValue(value: string) {
        const url = new URL(window.location.href);
        url.searchParams.set(this.queryName, value);
        window.location.href = url.href;
    }

    getData(): void {
        fetchRequest({
            requestURL: this.asyncURL!,
            resolveHandler: this.handleVariantsResponse.bind(this),
        });
    }

    handleVariantsResponse(response: TKResponse<ArticleVariantsResponse>) {
        if (!response || !response.success) return;
        const { variants, attributes } = response.dataAsJson;
        this.variants = variants;

        const variantDictionary = this.currentId ? this.getAllVariantsById(this.currentId) : undefined;
        const selectedAttributes = this.currentId ? this.getAttributesById(this.currentId) : undefined;

        if (!variantDictionary) return;
        attributes.forEach((item) => {
            const values = new Set<string>();
            variantDictionary[item.attr].forEach((value) => {
                values.add(value);
            });

            const selectedValue = selectedAttributes?.find((selectedItem) => selectedItem.attr === item.attr)?.value;

            const elements = this.createSelectbox({
                id: item.attr,
                label: item.label,
                values: Array.from(values),
                selectedValue,
            });

            if (!elements) return;

            this.selections.push({
                fieldElement: elements.fieldElement,
                wrapper: elements.wrapper,
                attribute: item.attr,
                label: item.label,
                values,
            } as Selection);
        });

        this.innerHTML = '';
        this.selections.forEach((item) => {
            this.insertAdjacentElement('beforeend', item.wrapper);
        });
    }

    /**
     * Retrieves all unique values of attributes related to a given attribute and value pair.
     *
     * @param {string} attribute - The attribute to match.
     * @param {string} value - The value of the attribute to match.
     * @returns {Record<string, Set<string>>}
     * An object where keys are attribute names and values are Sets of unique values associated with those attributes.
     */
    getValuesBySelection(attribute: string, value: string): Record<string, Set<string>> {
        return this.variants
            .filter((variant) => variant.attributes.some((attr) => attr.attr === attribute && attr.value === value))
            .flatMap((variant) => variant.attributes)
            .filter((attr) => attr.attr !== attribute && attr.value !== undefined)
            .reduce(
                (result, attr) => {
                    if (!result[attr.attr]) {
                        result[attr.attr] = new Set<string>();
                    }
                    if (!result[attr.attr].has(attr.value!)) {
                        result[attr.attr].add(attr.value!);
                    }
                    return result;
                },
                {} as Record<string, Set<string>>,
            );
    }

    /**
     * Creates a select box with the given configuration and attaches change event listener.
     *
     * @param {Object} params - The parameters for creating the select box.
     * @param {string} params.id - The ID for the select box element.
     * @param {string} params.label - The label for the select box element.
     * @param {string[]} params.values - The values to populate the select box options.
     * @param {string} [params.selectedValue] - The value to be selected by default, if any.
     * @returns {{ wrapper: HTMLElement, fieldElement: HTMLSelectElement } | undefined}
     * An object containing the wrapper and field element, or undefined if the field element is not found.
     */
    createSelectbox({
        id,
        label,
        values,
        selectedValue,
    }: {
        id: string;
        label: string;
        values: string[];
        selectedValue?: string;
    }):
        | {
              wrapper: HTMLElement;
              fieldElement: HTMLSelectElement;
          }
        | undefined {
        const wrapper = render(`
            <div class="tk-form-select">
                <label for="form-variants-${id}">${label}</label>
                <div class="tk-form__wrapper">
                    <select
                        id="form-variants-${id}"
                        name="form-variants-${id}"
                        data-tk-product-variants
                    >
                        ${values
                            .map(
                                (value) => `
                            <option value="${value}" ${selectedValue === value && 'selected'}>${value}</option>
                        `,
                            )
                            .join('\n')}
                    </select>
                </div>
            </div>
        `);
        const fieldElement = wrapper.querySelector('select');
        if (!fieldElement) return;
        const onChange = this.handleChangeSelection.bind(this);
        this.pushListener({ event: 'input', element: fieldElement, action: onChange });
        return {
            wrapper,
            fieldElement,
        };
    }

    async handleChangeSelection(event: InputEvent) {
        const target = event.target as HTMLSelectElement;
        const selectedOption = target.selectedOptions.item(0);
        const currentSelection = this.selections.find((item) => item.fieldElement === target);
        if (!currentSelection) return;
        const { attribute } = currentSelection;
        if (!selectedOption) return;
        const { value } = selectedOption;
        const id = this.getIdBySelection(attribute, value);
        if (!id) return;
        await this.getAsyncContentById(id);
    }

    /**
     * Retrieves the ID of the current variant based on the selected attributes.
     *
     * @returns {string | undefined} The ID of the matched variant, or undefined if no match is found.
     */
    getIdBySelection(attribute: string, value: string): string | undefined {
        const currentAttribute = {
            attr: attribute,
            value,
        } as Attribute;
        const attributes = [currentAttribute];

        const currentVariant = this.variants.find((variant) =>
            compareArrays<Attribute>(variant.attributes, attributes, ['attr', 'value']),
        );
        return currentVariant?.id;
    }

    /**
     * Retrieves all variants by the given ID, mapping the first attribute and its value to other related values.
     *
     * @param {string} id - The ID of the variant to retrieve related values for.
     * @returns {Record<string, Set<string>> | undefined} A record of attribute keys and their corresponding values, or undefined if no match is found.
     */
    getAllVariantsById(id: string): Record<string, Set<string>> | undefined {
        const variant = this.variants.find((variant) => variant.id === id);
        const firstAttribute = variant?.attributes.at(0);
        if (!firstAttribute) return;
        const { attr, value } = firstAttribute;
        if (!value) return;
        return {
            ...this.getValuesByAttribute(attr),
            ...this.getValuesBySelection(attr, value),
        };
    }

    /**
     * Retrieves all unique values associated with a specific attribute across all variants.
     *
     * @param {string} attribute - The attribute to retrieve values for.
     * @returns {Record<string, Set<string>>}
     * An object with the attribute as the key and a Set of its unique values, or undefined if no values are found.
     */
    getValuesByAttribute(attribute: string): Record<string, Set<string>> {
        const values = this.variants
            .flatMap((item) => item.attributes)
            .filter((item) => item.attr === attribute)
            .map((item) => item.value);
        return {
            [attribute]: new Set(values),
        } as Record<string, Set<string>>;
    }

    /**
     * Retrieves the attributes of a variant based on the given ID.
     *
     * @param {string} id - The ID of the variant to retrieve attributes for.
     * @returns {Attribute[] | undefined}
     * An array of attributes for the variant with the specified ID, or undefined if no variant is found.
     */
    getAttributesById(id: string): Attribute[] | undefined {
        return this.variants.find((item) => item.id === id)?.attributes;
    }

    async getAsyncContentById(id: string) {
        const asyncElements = document.querySelectorAll<HTMLElement>(`[${ASYNC_CONTENT_RELOAD_URL_ATTR}]`);
        const payload = {
            articleId: id,
        };
        const executer: Promise<void>[] = [];
        asyncElements.forEach((element) => {
            const url = element.getAttribute(ASYNC_CONTENT_RELOAD_URL_ATTR);
            if (!url) return;
            executer.push(
                fetchRequest({
                    requestURL: url,
                    resolveHandler: this.refreshContent.bind(this, element, id),
                    payload,
                }),
            );
        });
        await Promise.all(executer);
    }

    refreshContent(element: HTMLElement, id: string, response: TKResponse) {
        if (!response || !response.success) return;
        element.innerHTML = response.dataAsHtml;
        const url = new URL(window.location.href);
        url.searchParams.set(this.queryName, id);
        window.history.pushState(null, '', url.href);
    }
}
